import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Plus, Trash } from "@phosphor-icons/react";
import { Loader2 } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";

const baseUrl = import.meta.env.VITE_API_URL || "http://localhost:8000/api/v1/";

/**
 * Manage a product's selling units (Phase 3). Stock is kept in BASE units; each
 * alternate unit converts to base via "Units per base". Exactly one row is the
 * base unit (factor 1). A product with no rows just sells in its implicit base.
 */
export default function ProductUnitsManager({ productId, basePrice = 0, baseWholesale = 0 }) {
    const { toast } = useToast();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!productId) { setLoading(false); return; }
        (async () => {
            try {
                const res = await axios.get(`${baseUrl}product-units/${productId}`, { withCredentials: true });
                const data = res.data?.data || [];
                setRows(data.length ? data.map(normalize) : [seedBase()]);
            } catch {
                setRows([seedBase()]);
            } finally {
                setLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productId]);

    function seedBase() {
        return { name: "Base unit", factor_to_base: 1, price: basePrice || 0, wholesale_price: baseWholesale || "", barcode: "", is_base: 1 };
    }
    function normalize(u) {
        return {
            id: u.id,
            name: u.name || "",
            factor_to_base: parseFloat(u.factor_to_base) || 1,
            price: parseFloat(u.price) || 0,
            wholesale_price: u.wholesale_price ?? "",
            barcode: u.barcode || "",
            is_base: u.is_base ? 1 : 0,
        };
    }

    const update = (i, field, value) => {
        setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, [field]: value } : r)));
    };
    const setBase = (i) => {
        setRows((prev) => prev.map((r, idx) => ({ ...r, is_base: idx === i ? 1 : 0, factor_to_base: idx === i ? 1 : r.factor_to_base })));
    };
    const addRow = () => setRows((prev) => [...prev, { name: "", factor_to_base: "", price: "", wholesale_price: "", barcode: "", is_base: 0 }]);
    const removeRow = (i) => setRows((prev) => prev.filter((_, idx) => idx !== i));

    const save = async () => {
        // Client-side guard mirrors the server rules for a friendlier message.
        if (rows.filter((r) => r.is_base).length !== 1) {
            toast({ variant: "destructive", title: "Pick a base unit", description: "Exactly one unit must be the base (factor 1)." });
            return;
        }
        if (rows.some((r) => !r.is_base && (!parseFloat(r.factor_to_base) || parseFloat(r.factor_to_base) <= 0))) {
            toast({ variant: "destructive", title: "Invalid factor", description: "Each non-base unit needs 'units per base' greater than 0." });
            return;
        }
        setSaving(true);
        try {
            await axios.put(`${baseUrl}product-units/${productId}`, { units: rows }, { withCredentials: true });
            toast({ title: "Saved", description: "Selling units updated.", className: "bg-green-50 dark:bg-green-900/30 border-green-200" });
        } catch (e) {
            toast({ variant: "destructive", title: "Error", description: e.response?.data?.message || "Failed to save units" });
        } finally {
            setSaving(false);
        }
    };

    if (!productId) return null;

    return (
        <Card>
            <CardHeader>
                <CardTitle className="text-base">Selling Units</CardTitle>
                <CardDescription>
                    Sell this product in multiple units (e.g. piece + box). Stock is counted in the <strong>base</strong> unit;
                    "Units per base" is how many base units each unit contains (e.g. a box of 24 → 24).
                </CardDescription>
            </CardHeader>
            <CardContent>
                {loading ? (
                    <div className="flex items-center justify-center h-24"><Loader2 className="h-5 w-5 animate-spin" /></div>
                ) : (
                    <>
                        <div className="overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="w-20">Base?</TableHead>
                                        <TableHead>Unit name</TableHead>
                                        <TableHead className="w-32">Units per base</TableHead>
                                        <TableHead className="w-32">Price</TableHead>
                                        <TableHead className="w-32">Wholesale</TableHead>
                                        <TableHead className="w-36">Barcode</TableHead>
                                        <TableHead className="w-12"></TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {rows.map((r, i) => (
                                        <TableRow key={i}>
                                            <TableCell>
                                                <input type="radio" name="baseUnit" checked={!!r.is_base} onChange={() => setBase(i)} />
                                            </TableCell>
                                            <TableCell>
                                                <Input value={r.name} placeholder={r.is_base ? "e.g. Piece" : "e.g. Box (24)"}
                                                    onChange={(e) => update(i, "name", e.target.value)} />
                                            </TableCell>
                                            <TableCell>
                                                <Input type="number" min="0" step="0.001" value={r.is_base ? 1 : r.factor_to_base}
                                                    disabled={!!r.is_base}
                                                    onChange={(e) => update(i, "factor_to_base", e.target.value)} />
                                            </TableCell>
                                            <TableCell>
                                                <Input type="number" min="0" step="0.01" value={r.price}
                                                    onChange={(e) => update(i, "price", e.target.value)} />
                                            </TableCell>
                                            <TableCell>
                                                <Input type="number" min="0" step="0.01" value={r.wholesale_price}
                                                    onChange={(e) => update(i, "wholesale_price", e.target.value)} />
                                            </TableCell>
                                            <TableCell>
                                                <Input value={r.barcode} onChange={(e) => update(i, "barcode", e.target.value)} />
                                            </TableCell>
                                            <TableCell>
                                                {!r.is_base && (
                                                    <Button type="button" variant="ghost" size="icon" onClick={() => removeRow(i)}>
                                                        <Trash className="h-4 w-4 text-red-500" />
                                                    </Button>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                        <div className="flex items-center justify-between mt-3">
                            <Button type="button" variant="outline" size="sm" onClick={addRow}>
                                <Plus className="h-4 w-4 mr-1" /> Add unit
                            </Button>
                            <Button type="button" onClick={save} disabled={saving}>
                                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                                Save units
                            </Button>
                        </div>
                    </>
                )}
            </CardContent>
        </Card>
    );
}
